<script setup lang="ts">
// Home is Sierra's Bridge (plan 2E R18): a page head and a grid of panels, in Sierra's
// order, then any other widget in module order (design §8). A widget's component is
// loaded only for those who see it. The Modules panel, linking to each module the user
// can open, stays last. *Port:* the panel's "N ops scheduled · N requests to review"
// would need the shell to read modules' data, and the widgets show both.
import { useUser, visibleWidgets } from '@itc/sdk';
import { Icon, PageHead, Panel } from '@itc/ui';
import { computed, defineAsyncComponent, type Component } from 'vue';
import { RouterLink } from 'vue-router';
import { orderWidgets } from '../bridge.ts';
import { moduleNav, modules } from '../modules.ts';

const user = useUser();
const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
const shortcuts = computed(() => moduleNav.filter((item) => user.can(item.permission)));

// One async component per widget, made once, so re-rendering doesn't load it again.
const loaded = new Map<string, Component>();
const widgets = computed(() =>
  orderWidgets(visibleWidgets(modules, (permission) => user.can(permission))).map((widget) => {
    let component = loaded.get(widget.id);
    if (component === undefined) {
      component = defineAsyncComponent(widget.component);
      loaded.set(widget.id, component);
    }
    return { ...widget, view: component };
  }),
);
</script>

<template>
  <div class="home">
    <PageHead eyebrow="ITC Sierra Squadron" tagline="So others may fight" :title="`Welcome, ${user.user?.displayName ?? ''}`" :sub="today" />
    <div class="home__grid">
      <Panel v-for="widget in widgets" :key="widget.id" :title="widget.title" :class="{ 'home__wide': widget.span === 2 }" :data-widget="widget.id">
        <component :is="widget.view" />
      </Panel>
      <Panel title="Modules">
        <ul v-if="shortcuts.length > 0" class="home__links">
          <li v-for="item in shortcuts" :key="item.to">
            <RouterLink :to="item.to" class="home__link">
              <Icon :name="item.icon" />
              <span>{{ item.label }}</span>
            </RouterLink>
          </li>
        </ul>
        <p v-else class="home__empty">Nothing here for you yet. Modules you can use appear here.</p>
      </Panel>
    </div>
  </div>
</template>

<style scoped>
.home {
  max-width: 1180px;
}
.home__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
}
.home__wide {
  grid-column: span 2;
}
@media (max-width: 720px) {
  .home__wide {
    grid-column: auto;
  }
}
.home__empty {
  margin: 0;
  color: var(--itc-muted);
}
.home__links {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
/* Sierra's .op-item, as AdminLayout's section links. */
.home__link {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: var(--itc-tap);
  padding: 0 12px;
  border: 1px solid var(--itc-line);
  border-radius: var(--itc-r-control);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font-weight: 700;
  text-decoration: none;
}
.home__link:hover {
  border-color: var(--itc-accent);
}
</style>
